import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Input, Label, Nav, NavItem, NavLink, Spinner } from 'reactstrap'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'

import Modal from '../Modal'
import ApiService from '../../data/services/ApiService'

// ── Stripe setup ─────────────────────────────────────────────────────────────

const hostname = window && window.location && window.location.hostname
const configuredStripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
const stripePublishableKey = configuredStripeKey || (hostname === 'localhost'
    ? 'pk_test_AmmH2yRbUj2dudN847bdsEoG00STbHhfct'
    : 'pk_live_Qwwzo7PC35kHsv30h2Zo1Z3q00Ql6KxNty')
const stripePromise = loadStripe(stripePublishableKey)

// ── Payment method options per context ────────────────────────────────────────

const PM_CREDITS  = [
    { id: 'card', label: 'Tarjeta' },
    { id: 'spei', label: 'SPEI' },
    { id: 'oxxo', label: 'OXXO' }
]
const PM_CARD_ONLY = [
    { id: 'card', label: 'Tarjeta' }
]

// ── Inner modal (needs Stripe context) ───────────────────────────────────────

const ComprarTimbresModalInner = ({ isOpen, onRequestClose, pack: packProp, initialPack = null, onSuccess, purchaseType }) => {
    const stripe = useStripe()
    const elements = useElements()

    const pack = packProp || initialPack

    const isCredits      = purchaseType === 'credits'
    const PAYMENT_METHODS = isCredits ? PM_CREDITS : PM_CARD_ONLY

    const [step, setStep]               = useState('loading')
    const [paymentMethod, setPaymentMethod] = useState('card')
    const [savedCard, setSavedCard]     = useState(null)
    const [hadSavedCard, setHadSavedCard] = useState(false)
    const [saveCard, setSaveCard]       = useState(true)
    const [submitting, setSubmitting]   = useState(false)
    const [error, setError]             = useState(null)
    const [instructions, setInstructions] = useState(null)

    // Reset + load saved card whenever modal opens
    useEffect(() => {
        if (!isOpen) return
        setStep('loading')
        setError(null)
        setSubmitting(false)
        setSaveCard(true)
        setHadSavedCard(false)
        setPaymentMethod('card')
        setInstructions(null)

        ApiService.getMetodoPago()
            .then(res => {
                const data = res.data?.data
                if (data?.hasMethod) {
                    setSavedCard({
                        brand: data.brand,
                        last4: data.last4,
                        expMonth: data.expMonth,
                        expYear: data.expYear,
                        paymentMethodId: data.paymentMethodId
                    })
                    setHadSavedCard(true)
                    setStep('saved-card')
                } else {
                    setSavedCard(null)
                    setStep('new-card')
                }
            })
            .catch(() => {
                setSavedCard(null)
                setStep('new-card')
            })
    }, [isOpen])

    // Switch payment method tab
    const switchPaymentMethod = (pm) => {
        setPaymentMethod(pm)
        setError(null)
        if (pm === 'card') {
            setStep(hadSavedCard ? 'saved-card' : 'new-card')
        } else {
            setStep(`${pm}-form`)
        }
    }

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleConfirmWithSavedCard = async () => {
        setSubmitting(true)
        setError(null)
        try {
            const response = await ApiService.comprarTimbresConMetodoGuardado({
                quantity: pack.quantity,
                paymentMethodId: savedCard.paymentMethodId
            })
            toastr.success(`Compraste ${pack.quantity} timbres.`)
            onSuccess(response.data.data.newBalance)
            onRequestClose()
        } catch (err) {
            setError(err.response?.data?.message || 'No pudimos procesar el pago. Intenta de nuevo.')
        } finally {
            setSubmitting(false)
        }
    }

    const handlePayWithNewCard = async () => {
        if (!stripe || !elements) return
        setSubmitting(true)
        setError(null)
        try {
            const cardElement = elements.getElement(CardElement)
            const { error: stripeError, paymentMethod: newPm } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement
            })
            if (stripeError) {
                setError(stripeError.message)
                setSubmitting(false)
                return
            }
            const response = await ApiService.comprarTimbresConNuevaTarjeta({
                quantity: pack.quantity,
                paymentMethodId: newPm.id,
                saveCard
            })
            toastr.success(`Compraste ${pack.quantity} timbres.`)
            onSuccess(response.data.data.newBalance)
            onRequestClose()
        } catch (err) {
            setError(err.response?.data?.message || 'No pudimos procesar el pago. Intenta de nuevo.')
        } finally {
            setSubmitting(false)
        }
    }

    const handleConfirmAlternative = async () => {
        setSubmitting(true)
        setError(null)
        try {
            const response = await ApiService.comprarTimbresOxxoSpei({
                quantity: pack.quantity,
                paymentMethod  // 'spei' | 'oxxo'
            })
            setInstructions(response.data)
            setStep(`${paymentMethod}-instructions`)
        } catch (err) {
            setError(err.response?.data?.message || 'No pudimos generar las instrucciones. Intenta de nuevo.')
        } finally {
            setSubmitting(false)
        }
    }

    if (!pack) return null

    // ── Shared: purchase summary ──────────────────────────────────────────────

    const purchaseSummary = (
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e' }}>
                {pack.quantity} timbres adicionales
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#34c38f', marginTop: 4 }}>
                {pack.price} MXN
            </div>
        </div>
    )

    // ── Shared: payment method tabs (credits context only) ────────────────────

    const methodTabs = isCredits && !step.includes('instructions') && step !== 'loading' && (
        <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6 }}>
                Método de pago
            </div>
            <Nav tabs>
                {PAYMENT_METHODS.map(pm => (
                    <NavItem key={pm.id}>
                        <NavLink
                            active={paymentMethod === pm.id}
                            onClick={() => switchPaymentMethod(pm.id)}
                            style={{ cursor: 'pointer', fontSize: 13 }}
                        >
                            {pm.label}
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>
        </div>
    )

    // ── Loading ───────────────────────────────────────────────────────────────

    if (step === 'loading') {
        return (
            <Modal
                title="Comprar timbres"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                body={
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                        <Spinner color="primary" />
                    </div>
                }
                footer={null}
            />
        )
    }

    // ── SPEI instructions (post-confirm) ──────────────────────────────────────

    if (step === 'spei-instructions') {
        return (
            <Modal
                title="Instrucciones de pago — SPEI"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                body={
                    <>
                        {purchaseSummary}
                        <div style={{ background: '#e8f4fd', border: '1px solid #90CAF9', borderRadius: 6, padding: '16px 20px', marginBottom: 16 }}>
                            <p style={{ fontSize: 13, marginBottom: 12, color: '#555' }}>
                                Realiza una transferencia SPEI con los siguientes datos. Tu saldo se actualizará automáticamente cuando el banco confirme el pago.
                            </p>
                            <div style={{ display: 'grid', gap: 10 }}>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>CLABE</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'monospace', color: '#1a1a2e', letterSpacing: 1 }}>
                                        {instructions?.clabe}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Banco</div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{instructions?.banco}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Monto</div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{instructions?.monto}</div>
                                </div>
                            </div>
                        </div>
                        <p style={{ fontSize: 12, color: '#888', marginBottom: 0 }}>
                            También recibirás estas instrucciones por correo. Recibirás una notificación cuando se confirme tu pago.
                        </p>
                    </>
                }
                footer={
                    <Button color="secondary" onClick={onRequestClose}>Cerrar</Button>
                }
            />
        )
    }

    // ── OXXO instructions (post-confirm) ──────────────────────────────────────

    if (step === 'oxxo-instructions') {
        return (
            <Modal
                title="Voucher OXXO"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                body={
                    <>
                        {purchaseSummary}
                        <div style={{ background: '#fff8e1', border: '1px solid #FFB300', borderRadius: 6, padding: '16px 20px', marginBottom: 16 }}>
                            <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
                                Presenta el voucher en cualquier tienda OXXO para completar tu pago. Tu saldo se actualizará automáticamente cuando se confirme.
                            </p>
                            {instructions?.voucherUrl && (
                                <a
                                    href={instructions.voucherUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        display: 'inline-block',
                                        background: '#FFB300',
                                        color: '#fff',
                                        fontWeight: 700,
                                        padding: '10px 20px',
                                        borderRadius: 4,
                                        textDecoration: 'none',
                                        fontSize: 14
                                    }}
                                >
                                    Ver voucher OXXO
                                </a>
                            )}
                            {instructions?.expiresAt && (
                                <p style={{ fontSize: 12, color: '#888', marginTop: 10, marginBottom: 0 }}>
                                    Válido hasta: {instructions.expiresAt}
                                </p>
                            )}
                        </div>
                        <p style={{ fontSize: 12, color: '#888', marginBottom: 0 }}>
                            También recibirás el voucher por correo. Recibirás una notificación cuando se confirme tu pago.
                        </p>
                    </>
                }
                footer={
                    <Button color="secondary" onClick={onRequestClose}>Cerrar</Button>
                }
            />
        )
    }

    // ── SPEI form ─────────────────────────────────────────────────────────────

    if (step === 'spei-form') {
        return (
            <Modal
                title="Pagar con SPEI"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                body={
                    <>
                        {purchaseSummary}
                        {methodTabs}
                        <div style={{ background: '#e8f4fd', border: '1px solid #90CAF9', borderRadius: 6, padding: '12px 16px', fontSize: 13, color: '#555' }}>
                            Al confirmar, recibirás una CLABE interbancaria por correo. Tu saldo se actualizará automáticamente cuando el banco confirme la transferencia (puede tardar hasta 24 horas).
                        </div>
                        {error && (
                            <div style={{ color: '#B52C21', fontSize: 13, marginTop: 12 }}>
                                {error}
                            </div>
                        )}
                    </>
                }
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Button color="secondary" onClick={onRequestClose} disabled={submitting}>
                            Cancelar
                        </Button>
                        <Button color="primary" onClick={handleConfirmAlternative} disabled={submitting}>
                            {submitting
                                ? <><Spinner size="sm" /> Generando...</>
                                : 'Obtener datos de transferencia'
                            }
                        </Button>
                    </div>
                }
            />
        )
    }

    // ── OXXO form ─────────────────────────────────────────────────────────────

    if (step === 'oxxo-form') {
        return (
            <Modal
                title="Pagar en OXXO"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                body={
                    <>
                        {purchaseSummary}
                        {methodTabs}
                        <div style={{ background: '#fff8e1', border: '1px solid #FFB300', borderRadius: 6, padding: '12px 16px', fontSize: 13, color: '#555' }}>
                            Al confirmar, recibirás un voucher para pagar en cualquier tienda OXXO. Tu saldo se actualizará cuando se confirme el pago (máximo 72 horas).
                        </div>
                        {error && (
                            <div style={{ color: '#B52C21', fontSize: 13, marginTop: 12 }}>
                                {error}
                            </div>
                        )}
                    </>
                }
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Button color="secondary" onClick={onRequestClose} disabled={submitting}>
                            Cancelar
                        </Button>
                        <Button
                            color="warning"
                            onClick={handleConfirmAlternative}
                            disabled={submitting}
                            style={{ color: '#fff' }}
                        >
                            {submitting
                                ? <><Spinner size="sm" /> Generando...</>
                                : 'Generar voucher OXXO'
                            }
                        </Button>
                    </div>
                }
            />
        )
    }

    // ── Saved card ────────────────────────────────────────────────────────────

    if (step === 'saved-card') {
        return (
            <Modal
                title="Confirmar compra"
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                body={
                    <>
                        {purchaseSummary}
                        {methodTabs}

                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>Cobrar a:</div>
                            <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                {savedCard.brand} •••• {savedCard.last4}
                            </div>
                            <div style={{ fontSize: 13, color: '#888' }}>
                                Vence {savedCard.expMonth}/{savedCard.expYear}
                            </div>
                            <button
                                type="button"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#556ee6',
                                    padding: 0,
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    marginTop: 8
                                }}
                                onClick={() => setStep('new-card')}
                            >
                                Usar otra tarjeta
                            </button>
                        </div>

                        {error && (
                            <div style={{ color: '#B52C21', fontSize: 13, marginBottom: 12 }}>
                                {error}
                            </div>
                        )}
                    </>
                }
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Button color="secondary" onClick={onRequestClose} disabled={submitting}>
                            Cancelar
                        </Button>
                        <Button color="success" onClick={handleConfirmWithSavedCard} disabled={submitting}>
                            {submitting
                                ? <><Spinner size="sm" /> Procesando...</>
                                : `Confirmar compra — ${pack.price} MXN`
                            }
                        </Button>
                    </div>
                }
            />
        )
    }

    // ── New card ──────────────────────────────────────────────────────────────

    return (
        <Modal
            title="Agregar tarjeta y confirmar compra"
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            body={
                <>
                    {purchaseSummary}
                    {methodTabs}

                    <FormGroup>
                        <Label>Datos de tarjeta</Label>
                        <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: '10px 12px' }}>
                            <CardElement />
                        </div>
                    </FormGroup>

                    <FormGroup check style={{ marginBottom: 16 }}>
                        <Input
                            type="checkbox"
                            id="saveCard"
                            checked={saveCard}
                            onChange={e => setSaveCard(e.target.checked)}
                        />
                        <Label check htmlFor="saveCard">
                            Guardar tarjeta para futuras compras
                        </Label>
                    </FormGroup>

                    {error && (
                        <div style={{ color: '#B52C21', fontSize: 13, marginBottom: 12 }}>
                            {error}
                        </div>
                    )}
                </>
            }
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Button color="secondary" onClick={onRequestClose} disabled={submitting}>
                            Cancelar
                        </Button>
                        {hadSavedCard && (
                            <button
                                type="button"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#556ee6',
                                    padding: 0,
                                    cursor: 'pointer',
                                    fontSize: 13
                                }}
                                onClick={() => setStep('saved-card')}
                            >
                                ← Volver
                            </button>
                        )}
                    </div>
                    <Button color="success" onClick={handlePayWithNewCard} disabled={submitting}>
                        {submitting
                            ? <><Spinner size="sm" /> Procesando...</>
                            : `Pagar ${pack.price} MXN`
                        }
                    </Button>
                </div>
            }
        />
    )
}

// ── Exported wrapper with Stripe context ──────────────────────────────────────

const ComprarTimbresModal = (props) => (
    <Elements stripe={stripePromise}>
        <ComprarTimbresModalInner {...props} />
    </Elements>
)

export default ComprarTimbresModal
